import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { Card, plural } from './dashboard/primitives';
import type { PersonWork, WorkKind, WorkPayer, WorkRelation } from '../../services/billingApi';
import { exactUsd, usd } from '../../components/settings/usageParts';

const RELATION: Record<WorkRelation, string> = {
  internal: 'Your company',
  retainer: 'Retainer client',
  regular: 'Client',
  other: 'Not your client',
};

const KIND: Record<WorkKind, string> = {
  build: 'Build and QA',
  planning: 'Planning and chat',
  compute: 'Workspace compute',
};

const PAYER: Record<WorkPayer, string> = {
  you: 'charged to you',
  your_budget: 'from the project budget you fund',
  client_budget: "from the client's project budget",
  client: 'charged to the client',
  other: 'charged to another company',
};

const companyName = (company: PersonWork['companies'][number]): string => {
  if (company.relation === 'other') return 'Other companies';
  return company.name || 'Unnamed company';
};

const Tile: React.FC<{ label: string; value: number; note: string; strong?: boolean }> = ({ label, value, note, strong }) => (
  <div className={clsx('rounded-[12px] px-4 py-3 ring-1 ring-line', strong && 'bg-canvas')}>
    <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-eyebrow">{label}</div>
    <div className="mt-1 text-[18px] font-bold leading-6 tabular-nums text-ink">{usd(value)}</div>
    <div className="mt-0.5 text-[11.5px] text-ink-dim">{note}</div>
  </div>
);

/* Where one person's work went: their own company, retainer clients and other
 * clients, by project, with who paid for each part. */
const PersonWorkCard: React.FC<{ work: PersonWork; sub: string }> = ({ work, sub }) => {
  const [open, setOpen] = useState<Set<string>>(() => {
    if (work.companies.length > 3) return new Set();
    return new Set(work.companies.map((c) => c.id || 'other'));
  });
  const toggle = (key: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const { totals } = work;
  const paidNotes = [`${usd(totals.charged_to_you_usd)} charged to you`];
  if (totals.your_budgets_usd > 0) paidNotes.push(`${usd(totals.your_budgets_usd)} from project budgets you fund`);

  return (
    <Card title="Where their work went" sub={sub}>
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Internal" value={totals.internal_usd} note="Work for your company" />
        <Tile label="Retainer clients" value={totals.retainer_usd} note="Clients you pay for" />
        <Tile label="Other clients" value={totals.regular_usd + totals.other_usd} note="Clients who pay their own way" />
        <Tile label="Total work" value={totals.total_usd} note={paidNotes.join(' · ')} strong />
      </div>

      <div className="mt-4 divide-y divide-line-soft overflow-hidden rounded-[12px] border border-line">
        {work.companies.map((company) => {
          const key = company.id || 'other';
          const expanded = open.has(key);
          return (
            <div key={key}>
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={`work-${key}`}
                onClick={() => toggle(key)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/30"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[13px] font-semibold text-ink">{companyName(company)}</span>
                  <span className="shrink-0 rounded-full bg-canvas px-2 py-0.5 text-[10.5px] font-medium text-ink-dim ring-1 ring-line">{RELATION[company.relation]}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-[13px] font-semibold tabular-nums text-ink">{exactUsd(company.total_usd)}</span>
                  <ChevronDown className={clsx('h-3.5 w-3.5 text-ink-soft transition-transform', expanded && 'rotate-180')} />
                </span>
              </button>
              {expanded && (
                <div id={`work-${key}`} className="space-y-2 bg-canvas px-4 pb-3 pt-1">
                  {company.projects.map((project) => (
                    <div key={project.id || 'open'} className="rounded-[10px] bg-white px-3.5 py-2.5 ring-1 ring-line">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 truncate text-[12.5px] font-medium text-ink">{project.title || 'Open Workspace'}</span>
                        <span className="shrink-0 text-[12.5px] font-semibold tabular-nums text-ink">{exactUsd(project.total_usd)}</span>
                      </div>
                      <ul className="m-0 mt-1.5 list-none space-y-1 p-0">
                        {project.parts.map((part) => (
                          <li key={`${part.kind}-${part.paid_by}`} className="flex flex-col gap-0.5 text-[11.5px] sm:flex-row sm:items-baseline sm:justify-between">
                            <span className="text-ink-dim">
                              <span className="text-ink">{KIND[part.kind]}</span>, {PAYER[part.paid_by]}
                              {part.calls > 0 && <span className="text-ink-soft"> · {plural(part.calls, 'call', 'calls')}</span>}
                            </span>
                            <span className="tabular-nums text-ink-dim">{exactUsd(part.usd)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="m-0 mt-2.5 text-[11px] leading-4 text-ink-soft">
        Box time counts for whoever started the project or chat. Work before 27 October may still show as charged to the client where it is now charged to you.
      </p>
    </Card>
  );
};

export default PersonWorkCard;
